/**
 * @fileoverview Form builder validation schemas
 *
 * Schemas for form creation, field configuration, and submission management.
 * Used for multi-field surveys with conversion tracking and attribution.
 *
 * @module shared/schemas/forms
 */

import { z } from 'zod';

// =============================================================================
// Constants
// =============================================================================

/** Maximum name length */
export const MAX_FORM_NAME_LENGTH = 100;

/** Maximum description length */
export const MAX_FORM_DESCRIPTION_LENGTH = 500;

/** Maximum field label length */
export const MAX_FIELD_LABEL_LENGTH = 200;

/** Maximum field help text length */
export const MAX_FIELD_HELP_TEXT_LENGTH = 500;

/** Maximum number of options for select/radio/checkbox */
export const MAX_FIELD_OPTIONS = 50;

/** Maximum slot name length */
export const MAX_SLOT_NAME_LENGTH = 200;

/** Maximum slot description length */
export const MAX_SLOT_DESCRIPTION_LENGTH = 500;

/** Maximum slot category length */
export const MAX_SLOT_CATEGORY_LENGTH = 100;

/** Maximum comment length for slot claims */
export const MAX_CLAIM_COMMENT_LENGTH = 500;

// =============================================================================
// Field Type Definitions
// =============================================================================

/**
 * Available form field types
 */
export const FORM_FIELD_TYPES = [
  'text',
  'email',
  'textarea',
  'select',
  'checkbox',
  'radio',
  'number',
  'date',
  'poll_question',
] as const;

export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

/**
 * Basic field types (available to all tiers)
 */
export const BASIC_FIELD_TYPES: FormFieldType[] = ['text', 'email', 'textarea', 'select'];

/**
 * Advanced field types (Plus and above)
 */
export const ADVANCED_FIELD_TYPES: FormFieldType[] = ['checkbox', 'radio', 'number', 'date'];

/**
 * Field type display information
 */
export const FIELD_TYPE_INFO: Record<
  FormFieldType,
  { label: string; description: string; icon: string }
> = {
  text: { label: 'Short Text', description: 'Single line text input', icon: 'Type' },
  email: { label: 'Email', description: 'Email address with validation', icon: 'Mail' },
  textarea: { label: 'Long Text', description: 'Multi-line text area', icon: 'AlignLeft' },
  select: { label: 'Dropdown', description: 'Select from a list', icon: 'ChevronDown' },
  checkbox: { label: 'Checkboxes', description: 'Multiple choice selection', icon: 'CheckSquare' },
  radio: { label: 'Radio Buttons', description: 'Single choice selection', icon: 'Circle' },
  number: { label: 'Number', description: 'Numeric input', icon: 'Hash' },
  date: { label: 'Date', description: 'Date picker', icon: 'Calendar' },
  poll_question: {
    label: 'Poll Question',
    description: 'Voting question with options',
    icon: 'Vote',
  },
};

// =============================================================================
// Form Types (Survey vs Signup)
// =============================================================================

/**
 * Form types: survey for standard forms, signup for slot-based signups, poll for voting
 */
export const FORM_TYPES = ['survey', 'signup', 'poll'] as const;
export type FormType = (typeof FORM_TYPES)[number];

/**
 * Form type display information
 */
export const FORM_TYPE_INFO: Record<
  FormType,
  { label: string; description: string; icon: string }
> = {
  survey: {
    label: 'Survey Form',
    description: 'Collect responses with custom fields',
    icon: 'ClipboardList',
  },
  signup: {
    label: 'Signup Form',
    description: 'Slot-based signups for volunteers, potlucks, etc.',
    icon: 'Users',
  },
  poll: {
    label: 'Poll',
    description: 'Quick polls and voting with real-time results',
    icon: 'Vote',
  },
};

// =============================================================================
// Field Option Schema
// =============================================================================

/**
 * Option for select, checkbox, and radio fields
 */
export const fieldOptionSchema = z.object({
  value: z.string().min(1, 'Value is required').max(200),
  label: z.string().min(1, 'Label is required').max(200),
});

export type FieldOption = z.infer<typeof fieldOptionSchema>;

// =============================================================================
// Field Validation Schema
// =============================================================================

/**
 * Field validation rules
 */
export const fieldValidationSchema = z.object({
  minLength: z.number().int().positive().optional(),
  maxLength: z.number().int().positive().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  pattern: z.string().optional(),
});

export type FieldValidation = z.infer<typeof fieldValidationSchema>;

// =============================================================================
// Conditional Logic Schema
// =============================================================================

/**
 * Conditional logic rule for showing/hiding fields
 */
export const conditionalRuleSchema = z.object({
  fieldId: z.string(),
  operator: z.enum(['equals', 'not_equals', 'contains', 'is_empty', 'is_not_empty']),
  value: z.unknown().optional(),
});

export const conditionalLogicSchema = z.object({
  action: z.enum(['show', 'hide']),
  rules: z.array(conditionalRuleSchema),
  match: z.enum(['all', 'any']),
});

export type ConditionalRule = z.infer<typeof conditionalRuleSchema>;
export type ConditionalLogic = z.infer<typeof conditionalLogicSchema>;

// =============================================================================
// Poll Question Config Schema
// =============================================================================

/**
 * Configuration for poll questions (only used when field type is 'poll_question')
 */
export const pollQuestionConfigSchema = z.object({
  allowMultiple: z.boolean().default(false), // Single vs multi-choice
  maxSelections: z.number().int().min(1).optional(), // For multi-choice: max options user can select
});

export type PollQuestionConfig = z.infer<typeof pollQuestionConfigSchema>;

// =============================================================================
// Form Field Schema
// =============================================================================

/**
 * Complete form field schema
 */
export const formFieldSchema = z.object({
  id: z.string(),
  type: z.enum(FORM_FIELD_TYPES),
  label: z.string().min(1, 'Label is required').max(MAX_FIELD_LABEL_LENGTH),
  name: z.string().min(1, 'Field name is required').max(100),
  placeholder: z.string().max(200).optional(),
  helpText: z.string().max(MAX_FIELD_HELP_TEXT_LENGTH).optional(),
  required: z.boolean().default(false),
  options: z.array(fieldOptionSchema).max(MAX_FIELD_OPTIONS).optional(),
  validation: fieldValidationSchema.optional(),
  conditionalLogic: conditionalLogicSchema.optional(),
  order: z.number().int().min(0),
  // Poll-specific config (only used when type is 'poll_question')
  pollConfig: pollQuestionConfigSchema.optional(),
});

export type FormField = z.infer<typeof formFieldSchema>;

// =============================================================================
// Style Options Schema
// =============================================================================

export const fontFamilyOptions = ['inter', 'poppins', 'merriweather', 'system'] as const;
export const borderRadiusOptions = ['none', 'small', 'medium', 'large', 'full'] as const;
export const labelPositionOptions = ['top', 'left', 'floating'] as const;

export const formStyleOptionsSchema = z.object({
  backgroundColor: z.string().optional(),
  accentColor: z.string().optional(),
  textColor: z.string().optional(),
  fontFamily: z.enum(fontFamilyOptions).optional(),
  borderRadius: z.enum(borderRadiusOptions).optional(),
  labelPosition: z.enum(labelPositionOptions).optional(),
});

export type FormStyleOptions = z.infer<typeof formStyleOptionsSchema>;

// =============================================================================
// Form Settings Schema
// =============================================================================

export const formSettingsSchema = z.object({
  submitButtonText: z.string().max(50).default('Submit'),
  successMessage: z.string().max(500).default('Thank you for your submission!'),
  redirectUrl: z.string().url().optional().or(z.literal('')),
  maxSubmissions: z.number().int().positive().optional(),
  closesAt: z.string().datetime().optional(),
  notifyOnSubmission: z.boolean().optional(),
  notifyEmail: z.string().email().optional().or(z.literal('')),
  honeypotEnabled: z.boolean().default(true),
  allowMultipleSubmissions: z.boolean().default(true),

  // Signup form settings (only applicable when formType === 'signup')
  showClaimerNames: z.boolean().optional(),
  allowComments: z.boolean().optional(),
  requireEmail: z.boolean().optional(),
  sendConfirmationEmail: z.boolean().optional(),
  allowQuantitySelection: z.boolean().optional(),

  // Poll settings (only applicable when formType === 'poll')
  showResultsBeforeVote: z.boolean().optional(), // Show results before user votes
  showResultsAfterVote: z.boolean().optional(), // Show results after user votes (default true)
  requireEmailToVote: z.boolean().optional(), // Require email vs anonymous session voting
  allowVoteChange: z.boolean().optional(), // Allow voters to change their vote
  pollEndDate: z.string().datetime().optional(), // Poll closes at this date
});

export type FormSettings = z.infer<typeof formSettingsSchema>;

// =============================================================================
// Create/Update Form Schemas
// =============================================================================

/**
 * Schema for creating a new form
 */
export const createFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(MAX_FORM_NAME_LENGTH),
  description: z.string().max(MAX_FORM_DESCRIPTION_LENGTH).optional(),
  projectId: z.string().min(1, 'Project is required'),
  formType: z.enum(FORM_TYPES).default('survey'),
  linkId: z.string().optional(),
  fields: z.array(formFieldSchema).default([]),
  styleOptions: formStyleOptionsSchema.optional(),
  settings: formSettingsSchema.optional(),
  seoTitle: z.string().max(70).optional(),
  seoDescription: z.string().max(160).optional(),
});

export type CreateFormInput = z.infer<typeof createFormSchema>;

/**
 * Schema for updating an existing form
 */
export const updateFormSchema = z.object({
  name: z.string().min(1).max(MAX_FORM_NAME_LENGTH).optional(),
  description: z.string().max(MAX_FORM_DESCRIPTION_LENGTH).optional().nullable(),
  formType: z.enum(FORM_TYPES).optional(),
  linkId: z.string().optional().nullable(),
  fields: z.array(formFieldSchema).optional(),
  styleOptions: formStyleOptionsSchema.optional(),
  settings: formSettingsSchema.optional(),
  seoTitle: z.string().max(70).optional().nullable(),
  seoDescription: z.string().max(160).optional().nullable(),
});

export type UpdateFormInput = z.infer<typeof updateFormSchema>;

// =============================================================================
// Form Submission Status
// =============================================================================

export const SUBMISSION_STATUSES = ['new', 'read', 'archived', 'spam'] as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

export const SUBMISSION_STATUS_INFO: Record<
  SubmissionStatus,
  { label: string; color: string; icon: string }
> = {
  new: { label: 'New', color: 'blue', icon: 'Inbox' },
  read: { label: 'Read', color: 'gray', icon: 'Check' },
  archived: { label: 'Archived', color: 'purple', icon: 'Archive' },
  spam: { label: 'Spam', color: 'red', icon: 'AlertTriangle' },
};

// =============================================================================
// Slot Schemas (For signup forms)
// =============================================================================

/**
 * Schema for creating a new slot
 */
export const createSlotSchema = z.object({
  name: z.string().min(1, 'Name is required').max(MAX_SLOT_NAME_LENGTH),
  description: z.string().max(MAX_SLOT_DESCRIPTION_LENGTH).optional(),
  category: z.string().max(MAX_SLOT_CATEGORY_LENGTH).optional(),
  quantity: z.number().int().positive('Quantity must be at least 1'),
  startTime: z.string().datetime().optional(),
  endTime: z.string().datetime().optional(),
});

export type CreateSlotInput = z.infer<typeof createSlotSchema>;

/**
 * Schema for updating an existing slot
 */
export const updateSlotSchema = z.object({
  name: z.string().min(1).max(MAX_SLOT_NAME_LENGTH).optional(),
  description: z.string().max(MAX_SLOT_DESCRIPTION_LENGTH).optional().nullable(),
  category: z.string().max(MAX_SLOT_CATEGORY_LENGTH).optional().nullable(),
  quantity: z.number().int().positive().optional(),
  startTime: z.string().datetime().optional().nullable(),
  endTime: z.string().datetime().optional().nullable(),
});

export type UpdateSlotInput = z.infer<typeof updateSlotSchema>;

/**
 * Schema for claiming a slot
 */
export const claimSlotSchema = z.object({
  slotId: z.string().min(1, 'Slot is required'),
  quantity: z.number().int().positive().default(1),
  claimerName: z.string().min(1, 'Name is required').max(100),
  claimerEmail: z.string().email('Valid email is required').optional(),
  comment: z.string().max(MAX_CLAIM_COMMENT_LENGTH).optional(),
});

export type ClaimSlotInput = z.infer<typeof claimSlotSchema>;

/**
 * Slot claim status
 */
export const SLOT_CLAIM_STATUSES = ['confirmed', 'cancelled'] as const;
export type SlotClaimStatus = (typeof SLOT_CLAIM_STATUSES)[number];

// =============================================================================
// Form Response Types (from API)
// =============================================================================

/**
 * Slot as returned from API
 */
export interface FormSlotResponse {
  id: string;
  formId: string;
  name: string;
  description: string | null;
  category: string | null;
  quantity: number;
  claimedCount: number;
  remaining: number;
  startTime: string | null;
  endTime: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  claims?: SlotClaimResponse[];
}

/**
 * Slot claim as returned from API
 */
export interface SlotClaimResponse {
  id: string;
  slotId: string;
  quantity: number;
  claimerName: string | null;
  comment: string | null;
  status: SlotClaimStatus;
  createdAt: string;
}

/**
 * Form as returned from API
 */
export interface FormResponse {
  id: string;
  workspaceId: string;
  projectId: string;
  linkId: string | null;
  slug: string;
  name: string;
  description: string | null;
  formType: FormType;
  fields: FormField[];
  styleOptions: FormStyleOptions | null;
  settings: FormSettings | null;
  isPublished: boolean;
  isAcceptingResponses: boolean;
  totalViews: number;
  totalSubmissions: number;
  seoTitle: string | null;
  seoDescription: string | null;
  passwordHash: string | null;
  passwordHint: string | null;
  createdAt: string;
  updatedAt: string;
  project?: {
    id: string;
    name: string;
  };
  // For signup forms
  slots?: FormSlotResponse[];
}

/**
 * Form submission as returned from API
 */
export interface FormSubmissionResponse {
  id: string;
  formId: string;
  data: Record<string, unknown>;
  clickId: string | null;
  visitorToken: string | null;
  referrerCategory: string | null;
  referrerPlatform: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  country: string | null;
  city: string | null;
  device: string | null;
  status: SubmissionStatus;
  isSpam: boolean;
  submittedAt: string;
  createdAt: string;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Generate a unique field ID
 */
export function generateFieldId(): string {
  return `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create a new empty field
 */
export function createEmptyField(type: FormFieldType, order: number): FormField {
  const baseField: FormField = {
    id: generateFieldId(),
    type,
    label: '',
    name: `field_${order}`,
    required: false,
    order,
  };

  // Add default options for select/checkbox/radio
  if (['select', 'checkbox', 'radio'].includes(type)) {
    baseField.options = [
      { value: 'option_1', label: 'Option 1' },
      { value: 'option_2', label: 'Option 2' },
    ];
  }

  return baseField;
}

/**
 * Generate a slug-friendly field name from label
 */
export function generateFieldName(label: string): string {
  return (
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
      .substring(0, 50) || 'field'
  );
}

/**
 * Default style options
 */
export const DEFAULT_STYLE_OPTIONS: FormStyleOptions = {
  backgroundColor: '#ffffff',
  accentColor: '#10b981',
  textColor: '#1f2937',
  fontFamily: 'inter',
  borderRadius: 'medium',
  labelPosition: 'top',
};

/**
 * Default form settings
 */
export const DEFAULT_FORM_SETTINGS: FormSettings = {
  submitButtonText: 'Submit',
  successMessage: 'Thank you for your submission!',
  honeypotEnabled: true,
  allowMultipleSubmissions: true,
  // Signup form defaults
  showClaimerNames: true,
  allowComments: true,
  requireEmail: true,
  sendConfirmationEmail: false,
  allowQuantitySelection: false,
};

/**
 * Default signup form settings (overrides for signup forms)
 */
export const DEFAULT_SIGNUP_SETTINGS: Partial<FormSettings> = {
  submitButtonText: 'Sign Up',
  successMessage: 'You have been signed up! Check your email for a confirmation.',
  showClaimerNames: true,
  allowComments: true,
  requireEmail: true,
};

/**
 * Default poll settings (overrides for poll forms)
 */
export const DEFAULT_POLL_SETTINGS: Partial<FormSettings> = {
  submitButtonText: 'Vote',
  successMessage: 'Thank you for voting!',
  showResultsBeforeVote: false,
  showResultsAfterVote: true,
  requireEmailToVote: false,
  allowVoteChange: false,
};

// =============================================================================
// Slot Helper Functions
// =============================================================================

/**
 * Generate a unique slot ID
 */
export function generateSlotId(): string {
  return `slot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create a new empty slot
 */
export function createEmptySlot(
  order: number
): CreateSlotInput & { id: string; sortOrder: number } {
  return {
    id: generateSlotId(),
    name: '',
    quantity: 1,
    sortOrder: order,
  };
}

/**
 * Calculate remaining slots from quantity and claimed count
 */
export function calculateRemaining(quantity: number, claimedCount: number): number {
  return Math.max(0, quantity - claimedCount);
}

/**
 * Check if a slot is fully claimed
 */
export function isSlotFull(slot: FormSlotResponse): boolean {
  return slot.remaining <= 0;
}

/**
 * Group slots by category
 */
export function groupSlotsByCategory(
  slots: FormSlotResponse[]
): Map<string | null, FormSlotResponse[]> {
  const groups = new Map<string | null, FormSlotResponse[]>();

  for (const slot of slots) {
    const category = slot.category;
    const existing = groups.get(category);
    if (existing) {
      existing.push(slot);
    } else {
      groups.set(category, [slot]);
    }
  }

  return groups;
}

// =============================================================================
// Poll Helper Functions
// =============================================================================

/**
 * Generate a unique poll question ID
 */
export function generatePollQuestionId(): string {
  return `poll_q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create a new empty poll question
 */
export function createEmptyPollQuestion(order: number): FormField {
  return {
    id: generatePollQuestionId(),
    type: 'poll_question',
    label: '',
    name: `question_${order + 1}`,
    required: true,
    order,
    options: [
      { value: 'option_1', label: 'Option 1' },
      { value: 'option_2', label: 'Option 2' },
    ],
    pollConfig: {
      allowMultiple: false,
    },
  };
}

// =============================================================================
// Poll Result Types
// =============================================================================

/**
 * Vote count for a single option
 */
export interface PollOptionResult {
  value: string;
  label: string;
  voteCount: number;
  percentage: number;
}

/**
 * Results for a single poll question
 */
export interface PollQuestionResult {
  questionId: string;
  questionText: string;
  options: PollOptionResult[];
  totalVotes: number;
}

/**
 * Complete poll results response
 */
export interface PollResultsResponse {
  formId: string;
  questions: PollQuestionResult[];
  totalVoters: number;
  hasVoted: boolean;
  canVote: boolean;
  endDate: string | null;
  isEnded: boolean;
}
